import React, { useEffect, useMemo, useState } from 'react';
import { formatDate, useAdmin } from '../context';
import { Icon } from '../icons';
import { AuditEntry, CapId, StaffAudit, StaffMemberView, StaffState } from '../types';
import { Switch } from '../ui';
import {
  AUDIT_LABEL, CapState, DAY, DISCORD_ID_RE, cellState, describeExpiry, finalCaps, fromDateInput, grantable, normalizeOverrides, previewMenus, roleOf, sameSet, statusOf,
  toDateInput, withCapState,
} from './helpers';
import { RoleChip, StatusPill } from './parts';

export interface Draft {
  name: string;
  discordId: string;
  roleId: string | null;
  caps: CapId[];
  denied: CapId[];
  note: string;
  active: boolean;
  expiresAt: number | null;
}

const NAME_MAX = 48;
const NOTE_MAX = 160;

const fromMember = (m: StaffMemberView): Draft => ({
  name: m.name, discordId: m.discordId, roleId: m.roleId, caps: m.caps.slice(), denied: m.denied.slice(), note: m.note, active: m.active, expiresAt: m.expiresAt,
});

const sameDraft = (a: Draft, b: Draft): boolean =>
  a.name === b.name && a.roleId === b.roleId && a.note === b.note && a.active === b.active && a.expiresAt === b.expiresAt && sameSet(a.caps, b.caps) && sameSet(a.denied, b.denied);

interface Props {
  staff: StaffState;
  // null for a person who is not in the register yet
  member: StaffMemberView | null;
  seed: { name: string; discordId: string } | null;
  audit: StaffAudit | null;
  onClose: () => void;
  onSaved: (draft: Draft) => void;
}

const STATE_LABEL: Record<CapState, string> = { inherit: 'Hérité', add: 'Ajouté', deny: 'Retiré' };

export const MemberEditor: React.FC<Props> = ({ staff, member, seed, audit, onClose, onSaved }) => {
  const { act, confirm, send, onEscape } = useAdmin();
  const isNew = !member;
  const base: Draft = member ? fromMember(member) : { name: seed ? seed.name : '', discordId: seed ? seed.discordId : '', roleId: null, caps: [], denied: [], note: '', active: true, expiresAt: null };
  const [draft, setDraft] = useState<Draft>(base);
  const [now] = useState(Date.now());

  const dirty = isNew || !sameDraft(draft, base);

  // A fresh state from the server replaces the draft only when nothing was typed yet
  useEffect(() => {
    if (!dirty && member) setDraft(fromMember(member));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  useEffect(() => onEscape(onClose), [onEscape, onClose]);

  // The history of this person
  useEffect(() => {
    if (member) send({ action: 'adminStaffAuditRequest', discordId: member.discordId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member ? member.discordId : '']);

  const order = useMemo(() => grantable(staff), [staff]);
  const orderIds = useMemo(() => order.map((c) => c.id), [order]);
  const role = roleOf(staff, draft.roleId);
  const final = finalCaps(orderIds, role, draft.caps, draft.denied);
  const preview = previewMenus(draft.active ? final : []);
  const idOk = DISCORD_ID_RE.test(draft.discordId);
  const expired = !!draft.expiresAt && draft.expiresAt < now;

  const setRole = (id: string | null) => {
    const r = roleOf(staff, id);
    const n = normalizeOverrides(r, draft.caps, draft.denied);
    setDraft({ ...draft, roleId: id, caps: n.caps, denied: n.denied });
  };

  const setCap = (cap: CapId, state: CapState) => {
    const n = withCapState(role, draft.caps, draft.denied, cap, state);
    setDraft({ ...draft, caps: n.caps, denied: n.denied });
  };

  const save = () => {
    if (!idOk) return;
    act(
      'adminStaffUpsert',
      {
        discordId: draft.discordId,
        name: draft.name.trim(),
        roleId: draft.roleId,
        caps: draft.caps,
        denied: draft.denied,
        note: draft.note.trim(),
        active: draft.active,
        expiresAt: draft.expiresAt,
      },
      'Enregistrement'
    );
    onSaved(draft);
  };

  const remove = () => {
    if (!member) return;
    confirm({
      title: `Retirer ${member.name || member.discordId} du staff ?`,
      body: "Toutes ses permissions lui sont retirées immédiatement, y compris pendant qu'il est connecté.",
      confirmLabel: 'Retirer du staff',
      danger: true,
      onConfirm: () => {
        act('adminStaffRemove', { discordId: member.discordId }, 'Retrait du staff');
        onClose();
      },
    });
  };

  const setExpiryIn = (days: number | null) => setDraft({ ...draft, expiresAt: days === null ? null : Date.now() + days * DAY });

  // History: what the server answered for this person, else the recent entries that mention them
  const history: AuditEntry[] = useMemo(() => {
    if (!member) return [];
    if (audit && audit.discordId === member.discordId) return audit.entries;
    const keys = [member.discordId, member.name].filter(Boolean).map((k) => k.toLowerCase());
    return staff.audit.filter((e) => keys.indexOf(String(e.target).toLowerCase()) !== -1);
  }, [audit, member, staff.audit]);

  const status = member ? statusOf({ ...member, active: draft.active }) : draft.active ? 'active' : 'suspended';

  return (
    <aside className="admin-panel__editor" aria-label={isNew ? 'Nouveau membre' : `Édition de ${draft.name || draft.discordId}`}>
      <div className="admin-panel__editor-head">
        <div className="admin-panel__editor-id">
          <h3 className="admin-panel__editor-title">{isNew ? 'Nouveau membre' : draft.name || 'Sans nom'}</h3>
          <div className="admin-panel__editor-sub">
            <StatusPill status={expired && status === 'active' ? 'expired' : status} online={member ? member.online : false} />
            <RoleChip role={role} />
          </div>
        </div>
        <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={onClose}>Fermer</button>
      </div>

      <div className="admin-panel__editor-scroll">
        <section className="admin-panel__esec" aria-label="Identité">
          <h4 className="admin-panel__esec-title">Identité</h4>
          <label className="admin-panel__field-label" htmlFor="ap-st-name">Nom affiché</label>
          <input id="ap-st-name" className="admin-panel__input admin-panel__input--block" maxLength={NAME_MAX} value={draft.name} placeholder="Nom du personnage ou pseudo" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />

          <label className="admin-panel__field-label" htmlFor="ap-st-id">Identifiant Discord</label>
          <input
            id="ap-st-id"
            className="admin-panel__input admin-panel__input--block"
            inputMode="numeric"
            value={draft.discordId}
            readOnly={!isNew}
            aria-invalid={isNew && !idOk}
            placeholder="Obligatoire"
            onChange={(e) => setDraft({ ...draft, discordId: e.target.value.replace(/\s/g, '') })}
          />
          {isNew && !idOk ? (
            <p className="admin-panel__hint admin-panel__hint--warn">
              {draft.discordId ? 'Un identifiant Discord est composé de 5 à 25 chiffres.' : "Ce personnage n'a pas d'identifiant enregistré : saisissez son identifiant Discord."}
            </p>
          ) : null}
          {member ? (
            <p className="admin-panel__hint">
              {member.characters.length ? `Personnages : ${member.characters.join(', ')}. ` : 'Aucun personnage reconnu. '}
              Ajouté par {member.addedBy || 'inconnu'}, le {formatDate(member.addedAt)}.
            </p>
          ) : null}

          <label className="admin-panel__field-label" htmlFor="ap-st-note">Note</label>
          <textarea id="ap-st-note" className="admin-panel__input admin-panel__input--block admin-panel__textarea admin-panel__textarea--short" maxLength={NOTE_MAX} value={draft.note} placeholder="Par exemple : modératrice du soir" onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          <div className="admin-panel__counter">{draft.note.length} / {NOTE_MAX}</div>
        </section>

        <section className="admin-panel__esec" aria-label="Accès">
          <h4 className="admin-panel__esec-title">Accès</h4>
          <div className="admin-panel__switchrow">
            <div>
              <div className="admin-panel__matrix-label">Accès actif</div>
              <div className="admin-panel__matrix-desc">{draft.active ? 'La personne utilise le panel avec ses permissions.' : 'Suspendu : aucune permission, sans perdre le réglage.'}</div>
            </div>
            <Switch checked={draft.active} label="Accès actif" onChange={(v) => setDraft({ ...draft, active: v })} />
          </div>

          <div className="admin-panel__field-label">Échéance</div>
          <div className="admin-panel__expiry">
            <input
              className="admin-panel__input"
              type="date"
              aria-label="Date d'échéance de l'accès"
              value={toDateInput(draft.expiresAt)}
              onChange={(e) => setDraft({ ...draft, expiresAt: fromDateInput(e.target.value) })}
            />
            <button type="button" className="admin-panel__chip" onClick={() => setExpiryIn(7)}>+7 j</button>
            <button type="button" className="admin-panel__chip" onClick={() => setExpiryIn(30)}>+30 j</button>
            <button type="button" className="admin-panel__chip" aria-pressed={!draft.expiresAt} onClick={() => setExpiryIn(null)}>Sans échéance</button>
          </div>
          <p className={'admin-panel__hint' + (expired ? ' admin-panel__hint--warn' : '')}>
            {draft.expiresAt ? describeExpiry({ expiresAt: draft.expiresAt, expired }, now) + ` (${formatDate(draft.expiresAt)})` : 'Accès permanent.'}
          </p>
        </section>

        <section className="admin-panel__esec" aria-label="Rôle et permissions">
          <h4 className="admin-panel__esec-title">Rôle et permissions</h4>
          <label className="admin-panel__field-label" htmlFor="ap-st-role">Rôle</label>
          <div className="admin-panel__rolepick">
            <span className="admin-panel__swatch admin-panel__swatch--lg" style={{ background: role ? role.color : 'transparent' }} aria-hidden="true" />
            <select id="ap-st-role" className="admin-panel__select admin-panel__select--block" value={draft.roleId || ''} onChange={(e) => setRole(e.target.value || null)}>
              <option value="">Aucun rôle (permissions à la carte)</option>
              {staff.roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          {role && role.description ? <p className="admin-panel__hint">{role.description}</p> : null}

          <ul className="admin-panel__matrix admin-panel__matrix--tri" aria-label="Permissions par menu">
            {order.map((c) => {
              const st = (cellState(role, draft.caps, draft.denied, c.id) as string);
              const state = (st === 'deny' ? 'deny' : st === 'add' ? 'add' : 'inherit') as CapState;
              const inRole = !!role && role.caps.indexOf(c.id) !== -1;
              const on = final.indexOf(c.id) !== -1;
              return (
                <li key={c.id} className={'admin-panel__matrix-row' + (on ? ' admin-panel__matrix-row--on' : '')}>
                  <div className="admin-panel__matrix-text">
                    <div className="admin-panel__matrix-label">{c.label}</div>
                    <div className="admin-panel__matrix-desc">{role ? (inRole ? 'Accordé par le rôle' : 'Non accordé par le rôle') : c.description}</div>
                  </div>
                  <div className="admin-panel__tri" role="radiogroup" aria-label={`${c.label} : état par rapport au rôle`}>
                    {(['inherit', 'add', 'deny'] as CapState[]).map((s) => {
                      const disabled = (s === 'add' && inRole) || (s === 'deny' && !inRole);
                      return (
                        <button
                          key={s}
                          type="button"
                          role="radio"
                          aria-checked={state === s}
                          disabled={disabled}
                          title={disabled ? (s === 'add' ? 'Déjà accordé par le rôle' : "Le rôle ne l'accorde pas") : undefined}
                          className={'admin-panel__tri-opt admin-panel__tri-opt--' + s + (state === s ? ' admin-panel__tri-opt--on' : '')}
                          onClick={() => setCap(c.id, s)}
                        >
                          {STATE_LABEL[s]}
                        </button>
                      );
                    })}
                  </div>
                  <span className={'admin-panel__final' + (on ? ' admin-panel__final--on' : '')} title={on ? 'Autorisé au final' : 'Refusé au final'}>
                    <Icon name={on ? 'check' : 'minus'} size={14} />
                    <span className="admin-panel__sr">{on ? 'Autorisé' : 'Refusé'}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="admin-panel__hint">Le statut du serveur est visible par tout le staff. La gestion du staff n'est pas attribuable.</p>
        </section>

        <section className="admin-panel__esec" aria-label="Aperçu">
          <h4 className="admin-panel__esec-title">Ce que cette personne verra</h4>
          {!draft.active || expired ? (
            <p className="admin-panel__hint admin-panel__hint--warn">{!draft.active ? 'Accès suspendu : le panel ne lui est pas accessible.' : "Accès expiré : le panel ne lui est plus accessible."}</p>
          ) : final.length === 0 ? (
            <p className="admin-panel__hint admin-panel__hint--warn">Sans aucune permission, cette personne ne peut pas ouvrir le panel.</p>
          ) : null}
          <div className="admin-panel__previewline">
            <span className="admin-panel__previewlabel">Menus</span>
            <span className="admin-panel__chipline">{preview.menus.map((m) => <span key={m} className="admin-panel__tag">{m}</span>)}</span>
          </div>
          {preview.actions.length ? (
            <div className="admin-panel__previewline">
              <span className="admin-panel__previewlabel">Actions</span>
              <span className="admin-panel__chipline">{preview.actions.map((m) => <span key={m} className="admin-panel__tag">{m}</span>)}</span>
            </div>
          ) : null}
        </section>

        {member ? (
          <section className="admin-panel__esec" aria-label="Historique">
            <h4 className="admin-panel__esec-title">Historique de cette personne</h4>
            {history.length === 0 ? (
              <p className="admin-panel__hint">Aucune modification enregistrée.</p>
            ) : (
              <ul className="admin-panel__timeline admin-panel__timeline--small">
                {history.slice(0, 12).map((e, i) => (
                  <li key={i} className="admin-panel__tl-item">
                    <span className="admin-panel__tl-dot" aria-hidden="true" />
                    <div className="admin-panel__tl-main">
                      <div className="admin-panel__tl-title">{AUDIT_LABEL[e.action] || e.action}</div>
                      <div className="admin-panel__tl-detail">{e.detail}</div>
                      <div className="admin-panel__tl-meta">{e.by ? `${e.by}, ` : ''}{formatDate(e.at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </div>

      <div className="admin-panel__editor-foot">
        {member ? (
          <button type="button" className="admin-panel__button admin-panel__button--danger" onClick={remove}>Retirer du staff</button>
        ) : (
          <span />
        )}
        <div className="admin-panel__editor-save">
          {dirty && !isNew ? <span className="admin-panel__dirty">Modifications non enregistrées</span> : null}
          <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!idOk || !dirty} onClick={save}>
            {isNew ? 'Ajouter au staff' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </aside>
  );
};
